import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Button, Chip, Paper, Typography } from '@mui/material'
import { Spin } from 'antd'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'

import useAxios from '../../../../hooks/useAxios'
import PageUrls from '../../../../constants/page-urls'
import type { IBlock } from '../../types'
import type { SalepageThemeValues } from '../SalepageDetail/SeoThemeTab'
import { BlockRenderer } from './BlockRenderer'

interface SalepageResponse {
  id: string
  title: string
  slug: string
  status: 'draft' | 'published'
  blocks?: IBlock[]
  theme?: SalepageThemeValues
}

function useThemeStyles(theme: SalepageThemeValues | undefined) {
  React.useEffect(() => {
    if (!theme?.fontUrl || !theme.fontUrl.startsWith('https://fonts.googleapis.com')) return
    const id = 'salepage-preview-page-font'
    let link = document.getElementById(id) as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
    link.href = theme.fontUrl
    return () => link?.remove()
  }, [theme?.fontUrl])

  if (!theme) return {}
  const bg: React.CSSProperties = {}
  if (theme.backgroundGradient) {
    bg.background = theme.backgroundGradient
  } else if (theme.backgroundImage) {
    bg.backgroundImage = `url(${theme.backgroundImage})`
    bg.backgroundSize = theme.backgroundSize || 'cover'
    bg.backgroundPosition = theme.backgroundPosition || 'center'
    bg.backgroundRepeat = (theme.backgroundRepeat as React.CSSProperties['backgroundRepeat']) || 'no-repeat'
    if (theme.backgroundColor) bg.backgroundColor = theme.backgroundColor
  } else if (theme.backgroundColor) {
    bg.backgroundColor = theme.backgroundColor
  }
  return {
    fontFamily: theme.fontFamily || undefined,
    color: theme.fontColor || undefined,
    ...bg,
  }
}

const SalepagePreviewPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, isLoading } = useAxios<SalepageResponse>({
    method: 'GET',
    url: `/salepage/${id}`,
  })

  const themeStyles = useThemeStyles(data?.theme)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" />
      </Box>
    )
  }

  if (!data) {
    return (
      <Box sx={{ textAlign: 'center', py: 10 }}>
        <Typography variant="h6" color="text.secondary">ไม่พบข้อมูล Salepage</Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(PageUrls.Salepage.Index)}
          sx={{ mt: 2 }}
        >
          กลับไปรายการ
        </Button>
      </Box>
    )
  }

  const blocks = [...(data.blocks ?? [])].sort((a, b) => a.order - b.order)

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 4, px: 2, minHeight: '100vh', ...themeStyles }}>
      {/* Header Bar */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(PageUrls.Salepage.Index)}
        >
          กลับ
        </Button>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {data.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            /s/{data.slug}
          </Typography>
        </Box>
        <Chip
          label={data.status === 'published' ? 'Published' : 'Draft'}
          color={data.status === 'published' ? 'success' : 'default'}
          size="small"
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<EditIcon />}
          onClick={() => navigate(`${PageUrls.Salepage.Index}/${data.id}`)}
        >
          แก้ไข
        </Button>
      </Paper>

      {/* Blocks */}
      {blocks.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 6, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2 }}
        >
          <Typography variant="body1" color="text.secondary">
            ยังไม่มี Content Block ในหน้านี้
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {blocks.map((block, idx) => (
            <Box key={`${block.type}-${idx}`}>
              <BlockRenderer block={block} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default SalepagePreviewPage
